<template>
  <div class="flex-stretch">
    <ul class="nav nav-tabs mb-1">
      <li class="nav-item" v-for="mindmap in mindmaps" :key="mindmap.id">
        <a class="nav-link" :class="{ active: mindmap.id === selectedMindmap.id }" @click="selectedMindmap = mindmap">
          <i class="fas fa-fw fa-home mr-1 root" v-if="mindmap.isRoot"></i>
          {{ mindmap.notes.length }} notes
        </a>
      </li>
    </ul>
    <div id="mindmap" class="flex-stretch" ref="mindmap"></div>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import { trackEvent } from '@/utils/analytics'
import goToNote from '@/mixins/go-to-note'
import shortid from 'shortid'
import cytoscape from 'cytoscape'
import style from '@/assets/_variables.scss'

export default {
  props: ['note'],
  mixins: [goToNote],
  data() {
    return {
      width: 0,
      height: 0,
      selectedMindmap: { notes: [] }
    }
  },
  computed: {
    viewBox() {
      return `0 0 ${this.width} ${this.height}`
    },
    notes() {
      return this.$store.getters.notes
    },
    getNoteById() {
      return this.$store.getters.getNoteById
    },
    nodes() {
      const notes = this.selectedMindmap.notes.map(this.getNoteById)
      return notes.filter(({ id }) => {
        const noteHasLink = this.links.findIndex(link => link.source === id || link.target === id) >= 0
        return noteHasLink
      })
    },
    links() {
      const notes = this.selectedMindmap.notes.map(this.getNoteById)
      const links = [...notes].map(note => (
        note.links.to.map(to => ({
          source: note.id,
          target: to
        }))
      )).reduce((arr, elem) => arr.concat(elem), [])
      return links
    },
    mindmaps() {
      const mindmaps = this.notes.reduce((mindmaps, { id, links: { to, from } }) => {
        const atomicMindmap = [id, ...to, ...from]
        const indices = mindmaps.filter(mindmap => [...mindmap].filter(noteId => atomicMindmap.includes(noteId)).length > 0).map(mindmap => mindmaps.indexOf(mindmap))
        if (indices.length > 0) {
          const index = indices[0]
          const currentMindmap = indices.reduce((mindmap, index) => mindmap.concat(mindmaps[index]), [])
          indices.forEach((index, i) => {
            if (i !== 0) mindmaps.splice(index, 1)
          })
          mindmaps[index] = [...currentMindmap, ...atomicMindmap].filter((item, index, arr) => arr.indexOf(item) === index)
        } else {
          mindmaps.push(atomicMindmap)
        }
        return mindmaps
      }, [])

      return mindmaps
        .filter(mindmap => mindmaps.length > 1 ? mindmap.length > 1 : true)
        .sort((a, b) => b.length - a.length).slice(0, 5)
        .map(mindmap => {
          const isRoot = mindmap.includes(this.$store.getters.rootNote.id)
          return { id: shortid.generate(), notes: mindmap, isRoot }
        })
    }
  },
  created() {
    trackEvent('open mindmap', {
      eventCategory: 'mindmap',
      eventAction: 'show'
    })
    window.addEventListener('resize', this.handleResize)
  },
  mounted() {
    this.$nextTick(() => {
      this.width = this.$refs.mindmap.clientWidth
      this.height = this.$refs.mindmap.clientHeight
      this.selectedMindmap = this.mindmaps.find(mindmap => mindmap.notes.includes(this.note.id)) || this.mindmaps[0] // watcher will trigger render
    })
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    renderMindmap() {
      const mindmapCanvas = this.$refs.mindmap
      const boundingBox = {
        x1: 0,
        y1: 0,
        w: mindmapCanvas.clientWidth,
        h: mindmapCanvas.clientHeight
      }
      const deepCopy = e => JSON.parse(JSON.stringify(e))
      const links = this.links.map(deepCopy)
      const nodes = this.nodes.map(deepCopy)
      const elements = {
        nodes: [...nodes.map(node => ({ data: { id: node.id, title: node.title } }))],
        edges: [...links.map(link => ({ data: { id: `${link.source}-${link.target}`, ...link } }))]
      }
      const cy = cytoscape({
        container: mindmapCanvas,
        elements,
        layout: {
          name: 'cose',

          // Called on `layoutready`
          ready: function () { },

          // Called on `layoutstop`
          stop: function () { },

          // Whether to animate while running the layout
          // true : Animate continuously as the layout is running
          // false : Just show the end result
          // 'end' : Animate with the end result, from the initial positions to the end positions
          animate: false,

          // Easing of the animation for animate:'end'
          animationEasing: undefined,

          // The duration of the animation for animate:'end'
          animationDuration: undefined,

          // A function that determines whether the node should be animated
          // All nodes animated by default on animate enabled
          // Non-animated nodes are positioned immediately when the layout starts
          animateFilter: function (node, i) { return true },

          // The layout animates only after this many milliseconds for animate:true
          // (prevents flashing on fast runs)
          animationThreshold: 250,

          // Number of iterations between consecutive screen positions update
          refresh: 20,

          // Whether to fit the network view after when done
          fit: true,

          // Padding on fit
          padding: 30,

          // Constrain layout bounds; { x1, y1, x2, y2 } or { x1, y1, w, h }
          // boundingBox: undefined,
          boundingBox,

          // Excludes the label when calculating node bounding boxes for the layout algorithm
          nodeDimensionsIncludeLabels: false,

          // Randomize the initial positions of the nodes (true) or use existing positions (false)
          randomize: false,

          // Extra spacing between components in non-compound graphs
          componentSpacing: 40,

          // Node repulsion (non overlapping) multiplier
          nodeRepulsion: function (node) { return 2048 },

          // Node repulsion (overlapping) multiplier
          nodeOverlap: 4,

          // Ideal edge (non nested) length
          idealEdgeLength: function (edge) { return 32 },

          // Divisor to compute edge forces
          edgeElasticity: function (edge) { return 32 },

          // Nesting factor (multiplier) to compute ideal edge length for nested edges
          nestingFactor: 1.2,

          // Gravity force (constant)
          gravity: 1,

          // Maximum number of iterations to perform
          numIter: 1000,

          // Initial temperature (maximum node displacement)
          initialTemp: 1000,

          // Cooling factor (how the temperature is reduced between consecutive iterations
          coolingFactor: 0.99,

          // Lower temperature threshold (below this point the layout will end)
          minTemp: 1.0
        },
        // userZoomingEnabled: false,
        userPanningEnabled: false,
        pixelRatio: window.devicePixelRatio ? window.devicePixelRatio * 1.5 : 'auto',
        style: [ // the stylesheet for the graph
          {
            selector: 'node',
            style: {
              'background-color': style.gray600,
              'label': 'data(title)',
              'font-family': 'Source Sans Pro, sans-serif',
              'font-weight': '400',
              'font-size': '1.5em',
              'text-events': 'yes'
            }
          },
          {
            selector: '.current',
            style: {
              'background-color': style.contextedBlue
            }
          },
          {
            selector: '.mouseover',
            style: {
              'background-color': style.contextedBlue,
              'color': style.contextedBlue
            }
          },
          {
            selector: 'edge',
            style: {
              'width': 3,
              'line-color': style.gray400,
              'target-arrow-color': style.gray400,
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier'
            }
          }
        ]
      })
      cy.nodes().forEach(node => {
        if (node.data('id') === this.note.id) node.addClass('current')
      })
      cy.nodes().on('tap', event => {
        this.goToNote(event.target.data('id'))
      })
      cy.on('mouseover', 'node', event => {
        event.target.addClass('mouseover')
        mindmapCanvas.classList.add('mouseover')
      })
      cy.on('mouseout', 'node', event => {
        event.target.removeClass('mouseover')
        mindmapCanvas.classList.remove('mouseover')
      })
    },
    handleResize: debounce(function () {
      this.width = 0
      this.height = 0
      setTimeout(() => {
        this.width = this.$refs.mindmap.clientWidth
        this.height = this.$refs.mindmap.clientHeight
        this.renderMindmap()
      }, 0)
    }, 200),
    handleClick(event) {
      const sourceElement = event.srcElement
      const contextedLinkNoteId = sourceElement.getAttribute('data-contexted-link')
      if (contextedLinkNoteId) {
        trackEvent('go to note from mindmap', {
          eventCategory: 'mindmap',
          eventAction: 'go_to_note'
        })
        this.goToNote(contextedLinkNoteId)
      }
      if (sourceElement.tagName === 'A') window.location.href = sourceElement.getAttribute('href')
    }
  },
  watch: {
    selectedMindmap() {
      this.renderMindmap()
    }
  }
}
</script>
<style lang="scss">
#mindmap {
  width: 100%;
  // height: 400px;
}
.mouseover {
  cursor: pointer;
}
.link {
  fill: none;
  stroke: #666;
  stroke-width: 1.5px;
}

.mindmap text,
.mindmap circle {
  cursor: pointer;
}

circle {
  fill: #ccc;
  stroke: #333;
  stroke-width: 1.5px;
}
circle.current {
  fill: $primary;
}

text {
  /* font: 10px sans-serif; */
  /* pointer-events: none; */
  text-shadow: 0 1px 0 #fff, 1px 0 0 #fff, 0 -1px 0 #fff, -1px 0 0 #fff;
}

svg.mindmap {
  width: 100%;
  height: 100%;
  /* margin: 0 auto; */
}

h1.h1-breadcrumb {
  font-size: 0.8rem;
  color: $gray-400;
}
.note-title {
  border: 0px solid transparent;
  outline: 0px solid transparent;
}
.breadcrumb-title {
  cursor: pointer;
}
a.nav-link {
  color: $secondary !important;
}
a.nav-link.active {
  color: $primary !important;
}
</style>
